import apiService from "@services/apiService";

class TermAcknowledgementFormService {
    constructor() {}

    async getPendingForApproval(clubId) {
        if (!clubId) {
            throw new Error('Club ID is required');
        }

        const response = await apiService.get(`clubs/${clubId}/pending_term_acknowledgement_forms_for_approval`);
        return response.data;
    }

    async getClubInfo(clubId) {
        if (!clubId) {
            throw new Error('Club ID is required');
        }
        
        const response = await apiService.get(`clubs/${clubId}`);
        const data = response.data;
        
        return {
            club: data.data.club,
            ...this.formatRoles(data.data.roles)
        };
    }

    async submitTermAcknowledge(formData) {
        const membersAttributes = formData.assignedMembers.map(member => {
            const memberData = {
                user_id: member.userObject.id,
                role_key: member.roleId,
            };
            
            return memberData;
        });

        const params = {
            term_acknowledgement_form: {
                club_id: formData.clubId,
                term_id: formData.termId,
                members_attributes: membersAttributes
            }
        };

        const response = await apiService.post('term_acknowledgement_forms', params);
        return response.data.data.term_acknowledgement_form;
    }

    async getRequiredReplacement(clubId) {
        if (!clubId) {
            throw new Error('Club ID is required');
        }

        const response = await apiService.get(`term_acknowledgement_forms/required_replacement_for_club?club_id=${clubId}`);
        return response.data;
    }

    async restoreDraft(draftId) {
        const response = await apiService.get(`term_acknowledgement_forms/restore_draft/${draftId}`);
        const formattedData = this.formatData(response.data.data.draft);
        return formattedData;
    }

    async acceptRole(termAcknowledgementFormId) {
        const response = await apiService.post(`term_acknowledgement_forms/${termAcknowledgementFormId}/approve`);
        return response.data.data.term_acknowledgement_form;
    }

    async rejectRole(termAcknowledgementFormId) {
        const response = await apiService.post(`term_acknowledgement_forms/${termAcknowledgementFormId}/reject`);
        return response.data.data.term_acknowledgement_form;
    }

    async submitReplacementSigningAuthority(termAcknowledgementFormId, memberId, userId) {
        const params = {
            term_acknowledgement_form_member: {
                user_id: userId
            }
        };

        const response = await apiService.patch(`term_acknowledgement_forms/${termAcknowledgementFormId}/members/${memberId}`, params);
        return response.data.data.term_acknowledgement_form;
    }

    async getTermAcknowledgementForm(termAcknowledgementFormId) {
        if (!termAcknowledgementFormId) {
            throw new Error('Term Acknowledgement Form ID is required');
        }

        const response = await apiService.get(`term_acknowledgement_forms/${termAcknowledgementFormId}`);
        const formattedData = this.formatTermAcknowledgementFormData(response.data.data);
        return formattedData;
    }

    async getDashboard() {
        const response = await apiService.get('term_acknowledgements/dashboard');
        return response.data;
    }

    async getOpenTermAcknowledgementFormInfo(clubId) {
        if (!clubId) {
            throw new Error('Club ID is required');
        }

        const response = await apiService.get(`clubs/${clubId}/term_open_for_term_acknowledgement_form_submission`);
        const data = response.data;

        return {
            ...data,
            club: data.data?.club,
            ...this.formatRoles(data.data?.roles || [])
        };
    }

    async getTermAcknowledgementFormPermissions(clubId) {
        if (!clubId) {
            throw new Error('Club ID is required');
        }

        const response = await apiService.get(`clubs/${clubId}/term_acknowledgement_form_permissions`);
        return response.data;
    }

    // hold if additional data required formatting
    formatTermAcknowledgementFormData(data) {
        return {
            ...data.term_acknowledgement_form,
            ...this.formatRoles(data.club_roles || [])
        };
    }

    formatData(draft) {
        // format members data 
        const assignedMembers = draft.members.map(member => {
            return {
                id: member.id,
                userObject: member.user,
                roleId: member.role?.role_key || "general_member",
                roleName: member.role?.role_name || "General Member",
                roleType: member.role?.role_type || "general_member",
                isSigningAuthority: member.role?.signing_authority || false
            };
        });

        // extract unique roles from members
        const roles = draft.members
            .filter(member => member.role)
            .map(member => member.role)
            .filter((role, index, self) => 
                index === self.findIndex(r => r.role_key === role.role_key)
            )
            .map(role => ({
                id: role.id,
                roleName: role.role_name,
                roleId: role.role_key,
                roleType: role.role_type,
                responsibilities: role.responsibilities || [],
                isSigningAuthority: role.signing_authority || false,
                removable: false
            }));

        // extract signing authorities
        const signingAuthorities = roles
            .filter(role => role.isSigningAuthority)
            .map(role => role.roleId);

        return {
            clubId: draft.club.id,
            termId: draft.term_id,
            clubSigningAuthorities: signingAuthorities,
            clubRoles: roles,
            assignedMembers: assignedMembers
        };
    }

    formatRoles(roles) {
        const rolesMap = [];
        const signingAuthorities = [];

        roles.forEach(role => {
            const roleData = {
                id: role.id,
                roleName: role.name,
                roleId: role.role_key,
                roleType: role.role_type,
                responsibilities: role.responsibilities || [],
                signing_authority: role.signing_authority || false,
                removable: false
            };
            if (role.signing_authority) {
                signingAuthorities.push(role.role_key);
            }

            rolesMap.push(roleData);
        });
        rolesMap.push({
            roleName: "General Member",
            roleType: "general_member",
            roleId: "general_member",
            moreThanOne: true,
        });
        return {
            clubRoles: rolesMap,
            clubSigningAuthorities: signingAuthorities
        };
    }

    static create() {
        return new TermAcknowledgementFormService();
    }
}

export default TermAcknowledgementFormService;